import { FIELD_MAX } from './field-limits';
import { FIELD_PROBLEM, fieldSentence, isBlank, type FieldProblem } from './field-messages';
import { emailProblem } from './lead-form-validation';

/**
 * What a React form shows when its POST does not succeed. Pure, so every
 * branch is testable without a browser: the component hands over what
 * happened and gets back per-field messages plus one sentence for the alert.
 */

export const PHONE_DISPLAY = '800.849.8326';

export const SUBMIT_FAILURE_MESSAGE = {
  network: `We could not reach our server. Please check your connection and try again, or call us at ${PHONE_DISPLAY}.`,
  server: `Something went wrong on our side. Please try again in a moment, or call us at ${PHONE_DISPLAY}.`,
  fixFields: `Please fix the highlighted fields and send again, or call us at ${PHONE_DISPLAY}.`,
  unreadable: `Some details could not be read. Please check the form and try again, or call us at ${PHONE_DISPLAY}.`,
} as const;

const HTTP_UNPROCESSABLE = 422;
/** The route's generic 422 text (route-handler.ts); never shown to a visitor. */
export const VALIDATION_FAILED = 'Validation failed';
const HTTP_TOO_MANY_REQUESTS = 429;

/** The JSON a form route answers with when it refuses a submission (route-handler.ts). */
export interface FormErrorBody {
  readonly error?: string;
  /** Zod's flattened field errors: field name → schema messages. */
  readonly issues?: Readonly<Record<string, readonly string[] | undefined>>;
}

export type SubmitFailure =
  | { readonly kind: 'network' }
  | { readonly kind: 'response'; readonly status: number; readonly body: FormErrorBody | null };

export interface SubmitFailureView {
  /** Field name (as the form names its control) → plain message. */
  readonly fieldErrors: Record<string, string>;
  /** The sentence for the form's alert box; always offers the phone number. */
  readonly message: string;
}

/** Server fields the compact card collects in one "Full name" box. */
const COMPACT_NAME_PARTS: ReadonlySet<string> = new Set(['firstName', 'lastName']);
const COMPACT_NAME_CONTROL = 'name';

const MAX_BY_FIELD: Readonly<Record<string, number>> = {
  firstName: FIELD_MAX.name,
  lastName: FIELD_MAX.name,
  email: FIELD_MAX.email,
  company: FIELD_MAX.company,
  phone: FIELD_MAX.phone,
  eventCity: FIELD_MAX.eventCity,
  message: FIELD_MAX.message,
};

function problemFor(field: string, value: unknown): FieldProblem {
  if (isBlank(value)) return FIELD_PROBLEM.missing;
  const max = MAX_BY_FIELD[field];
  if (max !== undefined && typeof value === 'string' && value.trim().length > max) return FIELD_PROBLEM.tooLong;
  return FIELD_PROBLEM.other;
}

function messageFor(field: string, value: unknown): string | undefined {
  if (field === 'email' && typeof value === 'string' && value.trim() !== '') {
    const problem = emailProblem(value.trim());
    if (problem) return problem;
  }
  return fieldSentence(field, problemFor(field, value));
}

/**
 * Maps a failed submit onto the form. A 422's per-field issues land on the
 * matching controls (the compact card's first/last name on its one name
 * box); an issue for a field the visitor cannot see falls back to a general
 * sentence. Never shows a raw schema or browser message.
 */
export function describeSubmitFailure(
  failure: SubmitFailure,
  form: {
    /** What the form sent, keyed by server field name. */
    readonly values: Readonly<Record<string, unknown>>;
    /** True when the form has a visible control with this name. */
    readonly hasControl: (name: string) => boolean;
    readonly compact: boolean;
  },
): SubmitFailureView {
  if (failure.kind === 'network') return { fieldErrors: {}, message: SUBMIT_FAILURE_MESSAGE.network };
  const { status, body } = failure;
  if (status === HTTP_TOO_MANY_REQUESTS && body?.error) return { fieldErrors: {}, message: body.error };
  if (status !== HTTP_UNPROCESSABLE) return { fieldErrors: {}, message: SUBMIT_FAILURE_MESSAGE.server };

  // A refusal with no field issues (a retired showcase) is already written for visitors.
  if (!body?.issues && body?.error && body.error !== VALIDATION_FAILED) return { fieldErrors: {}, message: body.error };

  const fieldErrors: Record<string, string> = {};
  let unplaced = false;
  for (const [field, messages] of Object.entries(body?.issues ?? {})) {
    if (!messages || messages.length === 0) continue;
    const control = form.compact && COMPACT_NAME_PARTS.has(field) ? COMPACT_NAME_CONTROL : field;
    const message =
      control === COMPACT_NAME_CONTROL && form.compact
        ? 'Please check your first and last name.'
        : messageFor(field, form.values[field]);
    if (!message || !form.hasControl(control)) {
      unplaced = true;
      continue;
    }
    fieldErrors[control] ??= message;
  }
  const placed = Object.keys(fieldErrors).length > 0;
  const message = placed && !unplaced ? SUBMIT_FAILURE_MESSAGE.fixFields : SUBMIT_FAILURE_MESSAGE.unreadable;
  return { fieldErrors, message };
}
